// src/pages/Acoes/AcoesPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { excluirPlanoAcao, listarPlanosAcao } from '../../infra/api/PlanosAcao';
import { buscarColeta, mensagemErro } from '../../infra/api/Coletas';
import useResumoPlanosAcao from '../../hooks/useResumoPlanosAcao';
import usePaginasColeta from '../../hooks/usePaginasColeta';
import { usePermissao } from '../../security/usePermissao';
import { PAGINAS_COLETA } from '../../utils/coleta';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import PlanoAcaoCard from './components/PlanoAcaoCard';
import PlanoAcaoDialog from './components/PlanoAcaoDialog';
import ConcluirPlanoDialog from './components/ConcluirPlanoDialog';
import { MARGEM_LATERAL, TITULO_PAGINA } from '../../theme/escalaPagina';

const ABAS = [
    { valor: 'ABERTA', rotulo: 'Em aberto' },
    { valor: 'CONCLUIDA', rotulo: 'Concluídas' },
    { valor: null, rotulo: 'Todas' },
];
const TAMANHO = 20;

/**
 * Plano de ações do tambo: o que precisa ser feito quando um resultado pede atenção, quem
 * faz, até quando — e, concluída, o que foi feito e o resultado antes e depois. Fica
 * registrado para o controle da propriedade e orienta o assistente.
 */
export default function AcoesPage() {
    const { tenant } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { pode } = usePermissao();
    const podeGerenciar = pode('planoAcao.gerenciar');
    const podeExcluir = pode('planoAcao.excluir');
    const resumo = useResumoPlanosAcao(true);
    const { paginas } = usePaginasColeta();
    const unidadeDe = (pagina) => paginas[pagina]?.unidade || 'UFC/mL';

    const [aba, setAba] = useState(0);
    const [busca, setBusca] = useState('');
    const [termo, setTermo] = useState('');
    const [itens, setItens] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagina, setPagina] = useState(0);
    const [carregando, setCarregando] = useState(true);

    const [editor, setEditor] = useState(null); // { item?, amostra? }
    const [concluindo, setConcluindo] = useState(null);
    const [excluindo, setExcluindo] = useState(null);
    const [aviso, setAviso] = useState({ open: false, message: '', severity: 'info' });
    const avisar = (severity, message) => setAviso({ open: true, severity, message });

    // A busca espera a pessoa parar de digitar.
    useEffect(() => {
        const id = setTimeout(() => setTermo(busca.trim()), 350);
        return () => clearTimeout(id);
    }, [busca]);

    const carregar = useCallback(
        async (pag = 0) => {
            setCarregando(true);
            try {
                const r = await listarPlanosAcao({ status: ABAS[aba].valor, busca: termo, page: pag, size: TAMANHO });
                setItens((atuais) => (pag === 0 ? r.items : [...atuais, ...r.items]));
                setTotal(r.totalElements);
                setPagina(pag);
            } catch (e) {
                avisar('error', mensagemErro(e, 'Não foi possível carregar o plano de ações.'));
            } finally {
                setCarregando(false);
            }
        },
        [aba, termo]
    );

    useEffect(() => {
        carregar(0);
    }, [carregar]);

    // Vindo do painel ou do detalhe de uma amostra: /acoes?novo=<amostraId>.
    useEffect(() => {
        const amostraId = params.get('novo');
        if (!amostraId) return;
        const limpar = () => {
            const proximos = new URLSearchParams(params);
            proximos.delete('novo');
            setParams(proximos, { replace: true });
        };
        if (!podeGerenciar) {
            limpar();
            return;
        }
        buscarColeta(amostraId)
            .then((d) => setEditor({ amostra: { id: amostraId, identificacao: d.resumo?.identificacao, pagina: d.pagina } }))
            .catch(() => setEditor({}))
            .finally(limpar);
    }, [params, setParams, podeGerenciar]);

    const abrirAmostra = (r) => {
        const meta = PAGINAS_COLETA[r.pagina];
        if (meta) navigate(`/${tenant}/${meta.rota}/${r.amostraId}`);
    };

    const aoSalvar = (mensagem) => {
        setEditor(null);
        setConcluindo(null);
        avisar('success', mensagem);
        carregar(0);
    };

    const excluir = async () => {
        try {
            await excluirPlanoAcao(excluindo.plano.id);
            setExcluindo(null);
            avisar('success', 'Ação excluída.');
            carregar(0);
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível excluir a ação.'));
        }
    };

    return (
        <Box sx={{ px: MARGEM_LATERAL, py: { xs: 2, md: 3, xl: 4 }, maxWidth: 1400, mx: 'auto' }}>
            <SnackbarAlert
                open={aviso.open}
                onClose={() => setAviso((a) => ({ ...a, open: false }))}
                severity={aviso.severity}
                message={aviso.message}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                        Controle microbiológico
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={TITULO_PAGINA}>Plano de ações</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Ações, resultados e condições registradas orientam o assistente junto ao histórico de CBT.
                    </Typography>
                </Box>
                {podeGerenciar && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<Lucide.Plus size={18} />}
                        onClick={() => setEditor({})}
                        sx={{ borderRadius: 2, fontWeight: 700, flexShrink: 0 }}
                    >
                        Nova ação
                    </Button>
                )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mt: 3, mb: 2.5 }}>
                <Tabs value={aba} onChange={(_, v) => setAba(v)} variant="scrollable" allowScrollButtonsMobile>
                    {ABAS.map((a, i) => (
                        <Tab
                            key={a.rotulo}
                            value={i}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>{a.rotulo}</span>
                                    {a.valor === 'ABERTA' && resumo?.abertas > 0 && (
                                        <Chip size="small" label={resumo.abertas} sx={{ height: 20, fontWeight: 700 }} />
                                    )}
                                </Stack>
                            }
                        />
                    ))}
                </Tabs>
                <TextField
                    size="small"
                    placeholder="Buscar ação ou responsável"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    sx={{ width: { xs: '100%', md: 320 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lucide.Search size={18} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {carregando && itens.length === 0 ? (
                <Grid container spacing={2.5}>
                    {[0, 1].map((i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rounded" height={240} />
                        </Grid>
                    ))}
                </Grid>
            ) : itens.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                    <Lucide.ClipboardCheck size={32} />
                    <Typography sx={{ mt: 1 }}>
                        {termo ? 'Nenhuma ação encontrada.' : ABAS[aba].valor === 'ABERTA' ? 'Nenhuma ação em aberto.' : 'Nenhuma ação registrada.'}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Grid container spacing={2.5} alignItems="stretch">
                        {itens.map((item) => (
                            <Grid item xs={12} md={6} key={item.plano.id}>
                                <PlanoAcaoCard
                                    item={item}
                                    unidadeDe={unidadeDe}
                                    podeGerenciar={podeGerenciar}
                                    podeExcluir={podeExcluir}
                                    onConcluir={setConcluindo}
                                    onEditar={(i) => setEditor({ item: i })}
                                    onExcluir={setExcluindo}
                                    onAbrirAmostra={abrirAmostra}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {itens.length < total && (
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Button onClick={() => carregar(pagina + 1)} disabled={carregando}>
                                {carregando ? 'Carregando…' : 'Carregar mais'}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            <PlanoAcaoDialog
                open={Boolean(editor)}
                item={editor?.item}
                amostra={editor?.amostra}
                onClose={() => setEditor(null)}
                onSalvo={() => aoSalvar(editor?.item ? 'Ação atualizada.' : 'Ação criada.')}
            />
            <ConcluirPlanoDialog
                open={Boolean(concluindo)}
                item={concluindo}
                onClose={() => setConcluindo(null)}
                onSalvo={() => aoSalvar('Conclusão registrada.')}
            />
            <Dialog open={Boolean(excluindo)} onClose={() => setExcluindo(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>Excluir ação?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        "{excluindo?.plano?.titulo}" sai do plano de ações. O registro continua guardado para auditoria.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExcluindo(null)}>Cancelar</Button>
                    <Button color="error" onClick={excluir}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
